import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Badge,
    CircularProgress,
    IconButton,
    Paper,
    Stack
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    AccessTime as ClockIcon,
    Equalizer as ActivityIcon,
    Settings as SettingsIcon
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { sendCommand } from "../services/equipmentOperation";
import EquipmentActionPasswordConfirmation from "../components/EquipmentActionPasswordConfirmation";
import { SelectUsina } from "../components/SelectUsina";
import ElectricBoltSharpIcon from '@mui/icons-material/ElectricBoltSharp';
import { remoteOperations } from '../utils/typedUtils';

interface CommandRow {
    operation: string;
    type: 'boolean' | 'number';
    value: string | number;
    scope: 'one' | 'all';
    operationAlias: string;
    equipmentAlias: string;
    equipmentCod?: number;
    status?: 'idle' | 'executing' | 'completed' | 'failed';
    lastExecuted?: Date;
    [key: string]: any;
}

export default function RemoteControl() {
    const { usinaId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedUsina, setSelectedUsina] = useState(usinaId || '');
    const [selectedEquipment, setSelectedEquipment] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [open, setOpen] = useState(false);
    const [actionName, setActionName] = useState("");
    const [equipmentName, setEquipmentName] = useState("");
    const [equipmentCod, setEquipmentCod] = useState("");
    const [checkingPassword, setCheckingPassword] = useState(false);
    const [lastCommandTime, setLastCommandTime] = useState<Date | null>(null);
    const [remainingTime, setRemainingTime] = useState(0);
    const [payload, setPayload] = useState({
        equipment: "",
        equipmentCod: 0,
        value: 0,
        operation: ""
    });
    const [currentExecutingId, setCurrentExecutingId] = useState<string | null>(null);
    const [rows, setRows] = useState<Record<string, CommandRow[]>>(remoteOperations);

    // Timer countdown effect
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (lastCommandTime && remainingTime > 0) {
            interval = setInterval(() => {
                const now = new Date();
                const elapsed = Math.floor((now.getTime() - lastCommandTime.getTime()) / 1000);
                const remaining = Math.max(0, 60 - elapsed);

                setRemainingTime(remaining);

                if (remaining === 0) {
                    setLastCommandTime(null);
                    setCurrentExecutingId(null);
                }
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [lastCommandTime, remainingTime]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircleIcon color="success" fontSize="large" />;
            case 'executing':
                return <CircularProgress size={28} thickness={5} />;
            case 'failed':
                return <ErrorIcon color="error" fontSize="large" />;
            default:
                return <HourglassEmptyIcon color="disabled" fontSize="large" />;
        }
    };

    const isBlocked = remainingTime > 0;

    const filteredCommands = selectedEquipment && rows[selectedEquipment]
        ? rows[selectedEquipment].filter(cmd =>
            cmd.operation.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];

    const handleSendCommand = async (selectedUsina: string, row: CommandRow, event: React.MouseEvent) => {
        try {

            const commandPayload = {
                equipment: row.equipmentAlias,
                equipmentCod: Number(row.equipmentCod) || 0,
                value: Number(row.value),
                operation: row.operationAlias,
            }

            setPayload(commandPayload);
            setEquipmentCod(commandPayload.equipmentCod.toString() || '');
            setEquipmentName(selectedEquipment);
            setActionName(row.operation);
            setOpen(true);
        } catch (error) {
            enqueueSnackbar("Falha no sistema ao registrar a operação. Tente novamente ou reporte o problema.", { variant: "error" });
        }
    };

    const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number, field: string) => {
        const value = e.target.value;
        const isValid = /^[-0-9]*$/.test(value);

        let numericValue = Number(value);

        if (field !== "equipmentCod") {
            if (rows[selectedEquipment][index]["operationAlias"] !== "tracker" && (numericValue < -100 || numericValue > 100)) {
                enqueueSnackbar("Valor máximo permitido é 100%", { variant: "error" });
                numericValue = 100;
            }

            if (rows[selectedEquipment][index]["equipmentAlias"] === "tracker" && (numericValue < -55 || numericValue > 55)) {
                enqueueSnackbar("Valor deve estar entre -55 e 55 graus.", { variant: "error" });
                numericValue = 0;
            }
        }

        if (isValid) {
            setRows(prev => {
                const newRows = { ...prev };
                newRows[selectedEquipment][index][field] = numericValue;
                return newRows;
            });
        }
    };

    async function handleCheckPassword(password: string) {
        setCheckingPassword(true);
        try {
            const commandPayload = {
                password,
                ...payload
            }

            // Set status to executing before making the request
            setRows(prev => {
                const newRows = { ...prev };
                const cmdIndex = newRows[selectedEquipment].findIndex(c => c.operation === actionName);
                if (cmdIndex !== -1) {
                    newRows[selectedEquipment][cmdIndex].status = 'executing';
                    setCurrentExecutingId(`${selectedEquipment}-${actionName}`);
                }
                return newRows;
            });

            await sendCommand(selectedUsina, commandPayload);

            // Start timer only after successful request
            setLastCommandTime(new Date());
            setRemainingTime(60);

            // After timer completes, set status to completed
            setTimeout(() => {
                setRows(prev => {
                    const newRows = { ...prev };
                    const cmdIndex = newRows[selectedEquipment].findIndex(c => c.operation === actionName);
                    if (cmdIndex !== -1) {
                        newRows[selectedEquipment][cmdIndex].status = 'completed';
                        newRows[selectedEquipment][cmdIndex].lastExecuted = new Date();
                    }
                    return newRows;
                });
            }, 60000);

            enqueueSnackbar("Processo iniciado com sucesso!", {
                variant: "success",
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
            });
            setOpen(false);
        } catch (error) {
            setRows(prev => {
                const newRows = { ...prev };
                const cmdIndex = newRows[selectedEquipment].findIndex(c => c.operation === actionName);
                if (cmdIndex !== -1) {
                    newRows[selectedEquipment][cmdIndex].status = 'failed';
                    newRows[selectedEquipment][cmdIndex].lastExecuted = new Date();
                }
                return newRows;
            });

            setCurrentExecutingId(null);

            enqueueSnackbar("Acesso negado.", {
                variant: "error",
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
            });
        } finally {
            setCheckingPassword(false);
        }
    }

    return (
        <Paper sx={{ p: 3, background: '#eceef1', minHeight: '100vh' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Centro de Comandos
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Gerencie e execute comandos em seus equipamentos
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 }, alignItems: 'center' }}>
                        {isBlocked && (
                            <>
                                <ClockIcon color="action" />
                                <Badge badgeContent={`Bloqueado: ${remainingTime}s`} color="error" sx={{ mx: 6, px: 7, py: 0 }}>

                                </Badge>

                            </>
                        )}
                    </Box>
                </Box>

                {/* Filters and Search */}
                <Card sx={{ mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl sx={{ minWidth: 200 }}>
                                    <SelectUsina
                                        value={selectedUsina}
                                        onChange={(e: any) => setSelectedUsina(e.target.value as string)}
                                        width={100}
                                    />
                                </FormControl>
                                <FormControl sx={{ minWidth: 200 }} disabled={!selectedUsina}>
                                    <InputLabel>Equipamento</InputLabel>
                                    <Select
                                        value={selectedEquipment}
                                        onChange={(e) => setSelectedEquipment(e.target.value as string)}
                                        label="Equipamento"
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {selectedUsina && Object.keys(rows).map((equip) => (
                                            <MenuItem key={equip} value={equip}>{equip}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <TextField
                                fullWidth
                                placeholder="Buscar por operação..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Commands Grid */}
                {selectedEquipment ? (
                    <Grid container spacing={3}>
                        {filteredCommands.map((row, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                <Card className="command-card" sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                        boxShadow: 3
                                    }
                                }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                    <Box sx={{
                                                        p: 1,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(6,38,53,0.12)', // slate-100
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mr: 1
                                                    }}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth={2}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="lucide lucide-zap"
                                                            style={{ color: '#062635', width: 20, height: 20 }}
                                                        >
                                                            <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                                                        </svg>
                                                    </Box>
                                                    <Typography variant="subtitle2" color="#062635" sx={{ fontWeight: 'bold', letterSpacing: 0.5, }}>
                                                        {selectedEquipment.toUpperCase()}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
                                                        Operação
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h6" component="div" sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1.2,
                                                    mt: 0.5,
                                                    ml: 2,
                                                    color: '#062635'
                                                }}>
                                                    {row.operation.toUpperCase()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ minWidth: 40 }}>
                                                {getStatusIcon(row.status ?? 'idle')}
                                            </Box>
                                        </Stack>

                                        <Box sx={{ mb: 3 }}>
                                            {row.scope === 'one' && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                                        Código do Equipamento
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        value={row.equipmentCod || ''}
                                                        onChange={(e) => handleInputValueChange(e, index, 'equipmentCod')}
                                                        size="small"
                                                        disabled={isBlocked}
                                                    />
                                                </Box>
                                            )}

                                            <Box>
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                                    Valor
                                                </Typography>
                                                {row.type === 'number' ? (
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        value={row.value}
                                                        onChange={(e) => handleInputValueChange(e, index, 'value')}
                                                        size="small"
                                                        disabled={isBlocked}
                                                    />
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        value={row.value}
                                                        disabled
                                                        size="small"
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Badge

                                                sx={{
                                                    px: 0.5,
                                                    py: 0.2,
                                                    borderRadius: 2,
                                                    fontSize: 12,
                                                    fontWeight: 800,
                                                    bgcolor: 'transparent',
                                                    border: `1px solid`,
                                                    borderColor: 'text.secondary',
                                                    color: 'text.primary',
                                                    mt: 2,
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                Escopo: {row.scope === 'all' ? 'TODOS' : 'INDIVIDUAL'}
                                            </Badge>
                                        </Box>

                                        <Button
                                            fullWidth
                                            color="inherit"
                                            variant="contained"
                                            onClick={(e) => handleSendCommand(selectedUsina, row, e)}
                                            disabled={
                                                isBlocked ||
                                                (row.scope === 'one' && (!row.equipmentCod || !row.value)) ||
                                                (row.type === 'number' && !row.value) ||
                                                (currentExecutingId === `${selectedEquipment}-${row.operation}`)
                                            }
                                            sx={{
                                                mt: 2,
                                                py: 1.5,
                                                fontWeight: 'bold',
                                                backgroundColor: '#062635',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#111',
                                                },
                                            }}
                                        >
                                            {currentExecutingId === `${selectedEquipment}-${row.operation}` ?
                                                <CircularProgress size={24} color="inherit" /> :
                                                'Executar'
                                            }
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 8 }}>
                            <SearchIcon sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                Selecione uma usina e equipamento para ver os comandos disponíveis
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {selectedEquipment && filteredCommands.length === 0 && (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 8 }}>
                            <SearchIcon sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                Nenhum comando encontrado
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tente ajustar os filtros ou termo de busca
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                <EquipmentActionPasswordConfirmation
                    open={open}
                    onClose={() => setOpen(false)}
                    onConfirm={handleCheckPassword}
                    equipmentName={equipmentName}
                    equipmentCod={equipmentCod}
                    action={actionName}
                    checkingPassword={checkingPassword}
                />
            </Box>
        </Paper>
    );
}